import {EventEmitter, Injectable} from '@angular/core';
import {select, Store} from '@ngrx/store';
import {HttpClient, HttpErrorResponse, HttpParams, HttpRequest, HttpResponse} from '@angular/common/http';
import {forkJoin, Observable, of as observableOf, of, throwError} from 'rxjs';
import {catchError, concatAll, concatMap, filter, map, mergeMap, switchMap, takeUntil} from 'rxjs/operators';

import {UntypedFormGroup} from '@angular/forms';
import moment from 'moment';

import {isAuthenticated, State} from '@app/app.reducers';
import {environment} from '@env/environment';
import {
    areObjectsEqual,
    AttributeEntity,
    AttributeEntityItem,
    CanAction,
    DataAccessRequestFileLightModel,
    DataAccessRequestModel,
    DataAccessRequestStatus,
    IntegrationChangeEvent,
    IntegrationChangeTypes,
    IntegrationComplete,
    IntegrationDomain,
    IntegrationFormMode,
    IntegrationFormOptions,
    IntegrationWithRequestModel,
    OneAttributeEntity,
    OrgIdentity,
    PIIShieldAction,
    RequestedAction,
    RequestedArgumentModel,
    RequestedAttributeModel,
    RequestedFilterModel,
    RequestedOrganizationModel,
    RequestedPropertyModel,
    RequestedSchoolRoles,
    RequestedTaskModel,
    SchoolRole,
    SchoolSet
} from '../model/integration-models';
import {Counterparty, IntegrationProvider, IntegrationVendor, SchoolFilterType, SchoolProfileModel} from '../model/entity-profile';
import {ACTIVE_INTEGRATION_STATES, IntegrationState} from '@app/views/admin/application-settings/models/integration-state.model';
import {Application, POSSIP_APP_GUID} from '@app/views/admin/application-settings/models/application.model';
import {Integration, INTEGRATION_TASKS_ORDER, SsoAppAccessModes} from '@app/views/admin/application-settings/models/integration.model';
import {OrganizationsMapping} from '@app/views/admin/application-settings/models/organizationsMapping.model';
import {IntegrationTask} from '@app/views/admin/application-settings/models/integration-task.model';
import {DataSourceType, isFiltersAllowedForDataSource} from '@app/views/admin/application-settings/models/data-source-type.model';
import {IntegrationTaskMap} from '@app/views/admin/application-settings/models/integration-task-map.model';
import {DataAttribute} from '@app/views/admin/application-settings/models/data-attribute.model';
import {OrgsService} from './orgs.service';
import {RequestedFilterViewModel} from '@app/core/model/integration-models';
import {PredicateModel} from '@app/core/model/filtering/predicate-model';
import {MetadataModel} from '@app/core/model/filtering/metadata-model';
import {RestrictionSet} from '@app/core/model/filtering/restriction-set';
import {RestrictionRule} from '@app/core/model/filtering/restriction-rule';
import {RestrictionFilter} from '@app/core/model/filtering/restriction-filter';
import {
    AttributeType,
    FilterDefaultPredicateMap,
    FilterPredicate,
    FilterPredicateFriendlyName
} from '@app/core/model/filtering/filter-predicate';
import {
    EntityFilterModel,
    ExtraAttribute,
    FilterAction,
    FilterState,
    TASK_EXTRA_ATTRIBUTES
} from '@app/core/model/filtering/entity-filter-model';
import {FilteringChangeEvent, FiltersChangeTypes} from '@app/core/model/filtering/filtering-change-event';
import {SuggestionModel} from '@app/core/model/filtering/suggestion-model';
import {RulesAndTransforms} from '@app/core/model/filtering/rules-and-transforms';
import {FiltersListService} from '@app/views/admin/application-settings/components/filters/filters-list-wrapper/filters-list.service';
import {TokenService} from '@app/core/security/token.service';
import {LogService} from '@app/core/log/log.service';
import {FEATURE_TYPES} from '@app/views/admin/application-settings/models/application-properties.model';
import {SensitiveData} from '@app/views/admin/provider/sensitive-data/sensitive-data.model';
import {User} from '@app/core/model/user';
import {Preferences} from '@app/core/model/user-preferences.model';
import {PIIShieldService} from '@app/core/service/pii-shield.service';
import {PII_LEVEL} from '@app/views/admin/application-settings/components/basic/pii-shield-level-info/pii-shield.model';
import {DictionaryModel} from '@app/views/admin/application-settings/models/dictionary.model';
import {IntegrationMode} from '@app/views/admin/application-settings/models/integration-mode.model';
import {DefaultSchemaName} from '@app/views/admin/application-settings/models/data-source-config.model';

@Injectable()
export class IntegrationService {

    public onChangeIntegration: EventEmitter<IntegrationChangeEvent> = new EventEmitter<IntegrationChangeEvent>();

    static processFilterWithRelatedTask(filter: RequestedFilterModel, task: IntegrationTask, internal: IntegrationDomain, integration: Integration) {
        filter.taskIdentifier = task.sourceType + '-' + task.targetType;
        filter.eduToneEntityType = internal.getTaskSourceType(task);

        if ((integration.state === IntegrationState.PENDING) || (integration.state === IntegrationState.ORIGINAL)) {
            filter.isMyFilter = true;
            filter.taskId = filter.taskIdentifier;
        } else {
            filter.taskId = task.taskId;
        }
    }

    static getValidationDataAttributesList(task: IntegrationTask, action: FilterAction, eduToneDS: string,
        attributes: DataAttributeShort[] = []): DataAttributeShort[] {
        const taskAttributes: DataAttributeShort[] = IntegrationService.getValidationAttributesList(task.validations, action)
            .map((propertyName: string) => {
                const attribute = task[eduToneDS + 'AttributeList']
                    .find((attr: DataAttribute) => attr.propertyName === propertyName);
                const attrMapping = task.mappings
                    .find((mapping: IntegrationTaskMap) => mapping[eduToneDS + 'PropertyName'] === propertyName);
                let hasMapping = (!!attrMapping && attrMapping.enabled);
                if (!hasMapping && TASK_EXTRA_ATTRIBUTES[task[eduToneDS + 'Type']]) {
                    hasMapping = TASK_EXTRA_ATTRIBUTES[task[eduToneDS + 'Type']]
                        .some((attr: ExtraAttribute) => attr.propertyName === propertyName);
                }
                return {
                    propertyName: propertyName,
                    friendlyName: attribute ? attribute.friendlyName : propertyName,
                    hasMapping: hasMapping
                };
            }).filter((attribute: DataAttributeShort) =>
                !attributes.find((attr: DataAttributeShort) =>
                    (attr.propertyName === attribute.propertyName) && (attr.friendlyName === attribute.friendlyName))
            );

        attributes = attributes.filter((attribute: DataAttributeShort) =>
            !taskAttributes.find((attr: DataAttributeShort) => attr.propertyName === attribute.propertyName)
        );

        return [...attributes, ...taskAttributes];
    }

    static getValidationAttributesList(validations: EntityFilterModel[], action: FilterAction): string[] {
        return validations
            .filter((filterRule: EntityFilterModel) =>
                filterRule.action === action
                && filterRule.state === FilterState.ACTIVE
                && filterRule.propertyFilters && (filterRule.propertyFilters.length >= 1))
            .map((filterRule: EntityFilterModel) =>
                filterRule.propertyFilters[0].propertyName
            );
    }

    static compareByFriendlyName(a: DataAttributeShort, b: DataAttributeShort): number {
        return a.friendlyName.toLocaleLowerCase().localeCompare(b.friendlyName.toLocaleLowerCase());
    }

    static getEnabledToMaskEntityAttributeValuesObj(tasks: IntegrationTask[], piiShieldConfigEntitiesAttributesObj: object = {}): object {
        const enabledToMaskEntityAttributeValuesObj = {};
        tasks
            .forEach((task: IntegrationTask) => {
                if (!enabledToMaskEntityAttributeValuesObj[task.sourceType]) {
                    enabledToMaskEntityAttributeValuesObj[task.sourceType] = {};
                }
                const enabledToMaskAttributeValuesObj = enabledToMaskEntityAttributeValuesObj[task.sourceType];
                const piiShieldConfigEntityAttributesObj = piiShieldConfigEntitiesAttributesObj[task.sourceType] || {};

                task.mappings.forEach((mapping: IntegrationTaskMap) => {
                    if (!enabledToMaskAttributeValuesObj[mapping.sourcePropertyName]
                        && (mapping.piiShieldAction === PIIShieldAction.IS_SUPPORT)) {

                        const attributePIILevel = piiShieldConfigEntityAttributesObj[mapping.sourcePropertyName];
                        enabledToMaskAttributeValuesObj[mapping.sourcePropertyName] = attributePIILevel ? {piiLevel: attributePIILevel} : {};
                    }
                });
            });
        return enabledToMaskEntityAttributeValuesObj;
    }

    static getMaskedEntityAttributeValuesObj(attrEntities: AttributeEntity[]): {} {
        const maskedEntityAttributeValuesObj = {};
        attrEntities
            .forEach((attrEntity: AttributeEntity) => {
                if (!maskedEntityAttributeValuesObj[attrEntity.sourceType]) {
                    maskedEntityAttributeValuesObj[attrEntity.sourceType] = {};
                }
                const enabledToMaskAttributeValuesObj = maskedEntityAttributeValuesObj[attrEntity.sourceType];

                attrEntity.attributes.forEach((attribute: AttributeEntityItem) => {
                    if (!enabledToMaskAttributeValuesObj[attribute.sourcePropertyName]
                        && (attribute.piiShieldAction === PIIShieldAction.TRANSFORM)) {
                        enabledToMaskAttributeValuesObj[attribute.sourcePropertyName] = true;
                    }
                });
            });
        return maskedEntityAttributeValuesObj;
    }

    static capitalizeFirstLetter(string: string): string {
        return string.charAt(0).toUpperCase() + string.slice(1);
    }

    static needToUpdateUserData(oldUser: User, newUser: User): boolean {
        let result = true;
        if (oldUser) {
            const areUserObjectsEqual = IntegrationService.areUserObjectsWithoutInteractiveTourDataEqual(oldUser, newUser);

            result = !areUserObjectsEqual;
        }
        return result;
    }

    static needToUpdateUserPreferences(oldPreferences: Preferences, newPreferences: Preferences): boolean {
        let result = true;
        if (oldPreferences) {
            const arePreferencesObjectsEqual = IntegrationService.arePreferencesObjectsWithoutInteractiveTourDataEqual(
                oldPreferences, newPreferences);

            result = !arePreferencesObjectsEqual;
        }
        return result;
    }

    static areUserObjectsWithoutInteractiveTourDataEqual(oldUser: User, newUser: User): boolean {
        if (oldUser?.preferences?.completedInteractiveTours) {
            delete oldUser.preferences.completedInteractiveTours;
        }
        if (newUser?.preferences?.completedInteractiveTours) {
            delete newUser.preferences.completedInteractiveTours;
        }
        const areUserObjectsEqual = !!oldUser && areObjectsEqual(oldUser, newUser);
        return areUserObjectsEqual;
    }

    static arePreferencesObjectsWithoutInteractiveTourDataEqual(oldPreferences: Preferences, newPreferences: Preferences): boolean {
        if (oldPreferences?.completedInteractiveTours) {
            delete oldPreferences?.completedInteractiveTours;
        }
        if (newPreferences?.completedInteractiveTours) {
            delete newPreferences.completedInteractiveTours;
        }
        const arePreferencesObjectsEqual = !!oldPreferences && areObjectsEqual(oldPreferences, newPreferences);
        return arePreferencesObjectsEqual;
    }

    constructor(
        private orgsService: OrgsService,
        private store: Store<State>,
        private http: HttpClient,
        private logService: LogService,
        public tokenService: TokenService,
        public piiShieldService: PIIShieldService,
    ) {

    }

    /**
     * Retrieves default integration for current application
     * @param applicationId
     * @returns
     */
    getDefaultIntegration(applicationId: string): Observable<Integration> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/${applicationId}/integrations/original`;
        return this.http
            .get<Integration>(url)
            .pipe(
                map((integration: Integration) =>
                    this.applySpecificIntegrationFixes(integration)
                ),
                catchError(this.handleError),
                takeUntil(this.store.pipe(select(isAuthenticated), filter(isAuthenticated => !isAuthenticated)))
            );
    }

    applySpecificIntegrationFixes(integration: Integration): Integration {
        if ((integration.mode === IntegrationMode.EXPORT)
            && (integration.target.defaultSchemaName === DefaultSchemaName.BASIC_ROSTER)
            && (integration.applicationId === POSSIP_APP_GUID)
        ) {
            integration = this.fixPossipBasicRosterIntegration(integration);
        }
        return integration;
    }

    fixPossipBasicRosterIntegration(integration: Integration): Integration {
        const editableAttributesList = {
            'Staff': ['last_name', 'first_name', 'staff_rosters', 'school_id'],
            'Course': ['school_id'],
            'Roster': ['school_id', 'calendar_ref_id', 'section_course_id', 'teacher_id', 'school_id'],
            'RosterStudentEnrollment': ['section_id', 'student_id', 'school_id'],
            'Calendar': ['school_id'],
            'SchoolAdmin': ['last_name', 'first_name', 'admin_email', 'school_id']
        };
        if (integration?.tasks) {
            integration.tasks = integration.tasks.map((task: IntegrationTask) => {
                const editableAttr = editableAttributesList[task.sourceType];
                if (editableAttr?.length > 0) {
                    task.targetAttributeList = task.targetAttributeList.map((attr: DataAttribute) => {
                        if (attr.required) {
                            attr.required = (editableAttr.indexOf(attr.propertyName) === -1);
                        }
                        return attr;
                    });
                }
                return task;
            });
        }
        return integration;
    }

    /**
     * Retrieves integration for current application
     * @param applicationId
     * @param ownerGuid
     * @returns
     */
    getIntegration(applicationId: string, ownerGuid: string, withAttributes: boolean = true) {
        const params = new HttpParams().append('withAttributes', withAttributes);
        const options = {params: params};

        const url = `${environment.datahubBaseURI}/api/v2/applications/${applicationId}/integrations/current?ownerGuid=${ownerGuid}`;
        return this.http
            .get<Integration>(url, options)
            .pipe(
                map((integration: Integration) => {
                    integration = this.applySpecificIntegrationFixes(integration);

                    if (integration && integration.tasks) {
                        if (integration.dictionaries) {
                            integration.dictionaries = integration.dictionaries.filter((dictionary: DictionaryModel) =>
                                !dictionary.dictionaryName.startsWith('deleted_')
                            );
                        }
                        const edutoneDS: string = (integration.source.dataSourceType === DataSourceType.EduTone) ? 'source' : 'target';

                        integration.tasks.sort((task1: IntegrationTask, task2: IntegrationTask) =>
                            (INTEGRATION_TASKS_ORDER[task1[edutoneDS + 'Type']] || 100)
                            - (INTEGRATION_TASKS_ORDER[task2[edutoneDS + 'Type']] || 100));
                    }
                    return integration;
                }),
                catchError(this.handleError)
            );
    }

    getIntegrationById(integrationId: string) {
        const url = `${environment.datahubBaseURI}/api/v2/applications/integrations/${integrationId}`;
        return this.http
            .get<Integration>(url)
            .pipe(
                map((integration: Integration) => {
                    if (integration?.dictionaries) {
                        integration.dictionaries = integration.dictionaries.filter((dictionary: DictionaryModel) =>
                            !dictionary?.dictionaryName?.startsWith('deleted_')
                        );
                    }
                    return integration;
                }),
                catchError(this.handleError)
            );
    }

    createIntegration(newIntegration: Integration) {
        const url = `${environment.datahubBaseURI}/api/v2/applications/integrations`;
        return this.http
            .post<Integration>(url, newIntegration)
            .pipe(catchError(this.handleError));
    }

    createIntegrationPending(newIntegration: Integration, ownerGuid: string) {
        const url = `${environment.datahubBaseURI}/api/v2/applications/integrations/pending?ownerGuid=${ownerGuid}`;
        return this.http
            .post<Integration>(url, newIntegration)
            .pipe(catchError(this.handleError));
    }

    sendRequestWithIntegration(requestModel: IntegrationWithRequestModel, ownerGuid: string): Observable<IntegrationWithRequestModel> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/requests/send?ownerGuid=${ownerGuid}`;
        return this.http
            .post<IntegrationWithRequestModel>(url, requestModel)
            .pipe(catchError(this.handleError));
    }

    uploadFilesAfterRequest(
        requestModel: IntegrationWithRequestModel,
        orgId: string,
        files: DataAccessRequestFileLightModel[]
    ): Observable<HttpResponse<DataAccessRequestFileLightModel[]>> {
        const integrationId = requestModel.integrationModel ? requestModel.integrationModel.integrationId : requestModel.requestModel.integrationId;
        const requestId = requestModel.requestModel.guid;
        const url = `${environment.datahubBaseURI}/api/v2/applications/requests/save-files?orgId=${orgId}&integrationId=${integrationId}&requestId=${requestId}`;

        const data = new FormData();
        files.forEach((fileItem: DataAccessRequestFileLightModel) => {
            data.append('files', fileItem.file._file);
        });

        const req = new HttpRequest('POST', url, data);
        return <Observable<HttpResponse<DataAccessRequestFileLightModel[]>>>this.http.request(req)
            .pipe(catchError(this.handleError));
    }

    downloadRequestAttachment(file: DataAccessRequestFileLightModel): Observable<any> {
        console.log('Download file' + file.fileName);

        if (file.fileName.endsWith('.pdf')
            || file.fileName.endsWith('.doc')
            || file.fileName.endsWith('.docx')
            || file.fileName.endsWith('.png')
            || file.fileName.endsWith('.jpg')) {

            const url = `${environment.datahubBaseURI}/api/v2/applications/requests/download/file/${file.guid}`;
            const options = {responseType: 'blob' as const};
            const extension = file.fileName.substr(-4);

            return this.http.get(url, options).pipe(
                map((res: any) => new Blob([res], {type: `application/${extension}`}))
            );
        } else {
            console.warn('File extension is not allowed to download.');
            return of(null);
        }
    }

    saveFileOnComputer(blob, fileName: string, mimeType: string = '') {
        // It is necessary to create a new blob object with mime-type explicitly set
        // otherwise only Chrome works like it should
        if (!mimeType) {
            const pos = fileName.lastIndexOf('.');
            if (!pos) {
                mimeType = 'text/plain';
            } else {
                const ext = fileName.substr(fileName.lastIndexOf('.') + 1);
                mimeType = `application/${ext}`;
            }
        }
        const newBlob = new Blob([blob], {type: mimeType});

        // IE doesn't allow using a blob object directly as link href
        // instead it is necessary to use msSaveOrOpenBlob
        if (window.navigator && (<any>window.navigator).msSaveOrOpenBlob) {
            (<any>window.navigator).msSaveOrOpenBlob(newBlob);
            return;
        }

        // For other browsers:
        // Create a link pointing to the ObjectURL containing the blob.
        const data = window.URL.createObjectURL(newBlob);
        const link = document.createElement('a');
        link.href = data;
        link.target = '_blank';
        link.download = fileName;
        link.click();
        setTimeout(function() {
            // For Firefox it is necessary to delay revoking the ObjectURL
            window.URL.revokeObjectURL(data);
        }, 100);
    }

    sendRequest(dataAccessRequest: DataAccessRequestModel): Observable<DataAccessRequestModel> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/requests`;
        return this.http
            .post<DataAccessRequestModel>(url, dataAccessRequest)
            .pipe(catchError(this.handleError));
    }

    getRequestListForCurrOrg(status: DataAccessRequestStatus) {
        const url = `${environment.datahubBaseURI}/api/v2/applications/requests?status=${status}`;
        return this.http
            .get<any>(url)
            .pipe(catchError(this.handleError));
    }

    getRequestListForOrg(orgGuid: string): Observable<DataAccessRequestModel[]> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/requests/${orgGuid}`;
        return this.http
            .get<any>(url)
            .pipe(catchError(this.handleError));
    }

    getRequest(requestGuid: string): Observable<DataAccessRequestModel> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/requests/${requestGuid}`;
        return this.http
            .get<any>(url)
            .pipe(catchError(this.handleError));
    }

    approveRequest(requestGuid: string, replyMessage: string, changedFilters: RequestedFilterModel[] = [], expirationTs?: Date):
        Observable<DataAccessRequestModel> {

        const params = this.getQueryParams({
            replyMessage: encodeURIComponent(replyMessage),
            expirationTs: expirationTs ? this.convertExpirationDateToTimestamp(expirationTs) : ''
        });
        const url = `${environment.datahubBaseURI}/api/v2/applications/requests/${requestGuid}/approve?${params}`;

        return this.http
            .put<any>(url, changedFilters)
            .pipe(catchError(this.handleError));
    }

    rejectRequest(requestGuid: string, replyMessage: string): Observable<DataAccessRequestModel> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/requests/${requestGuid}/reject`;
        return this.http
            .put<any>(url, replyMessage)
            .pipe(catchError(this.handleError));
    }

    deleteRequest(requestGuid: string) {
        const url = `${environment.datahubBaseURI}/api/v2/applications/requests/${requestGuid}`;
        return this.http
            .delete(url)
            .pipe(catchError(this.handleError));
    }

    saveExpirationDate(integrationId: string, expirationTs: Date): Observable<any> {
        const convertedTs = expirationTs ? this.convertExpirationDateToTimestamp(expirationTs) : '';
        const url = `${environment.datahubBaseURI}/api/v2/applications/integrations/${integrationId}/expiration?date=${convertedTs}`;

        return this.http
            .put<any>(url, null)
            .pipe(catchError(this.handleError));
    }

    /**
     * Retrieves list of requests for sender
     * @param orgGuid
     * @param appGuid
     * @param institutionGuid
     * @param status, '' (empty string) - get all requests (PENDING, REJECTED, APPROVED)
     * @returns
     */
    getRequestListForSender(orgGuid: string, status?: string, appGuid?: string, institutionGuid?: string): Observable<DataAccessRequestModel[]> {
        let params = {};
        appGuid = appGuid != null && typeof appGuid !== 'undefined' ? appGuid : '';
        status = status != null && typeof status !== 'undefined' ? status : '';
        if (appGuid) {
            params = {'appGuid': appGuid, 'status': status};
        } else if (institutionGuid) {
            params = {'institutionGuid': institutionGuid, 'status': status};
        } else {
            params = {'status': status};
        }
        const url = `${environment.datahubBaseURI}/api/v2/applications/requests/sender/${orgGuid}`;

        return this.http
            .get<any>(url, {params: params})
            .pipe(
                map((requests: DataAccessRequestModel[]) =>
                    this.trimRequestMessages(requests)
                ),
                catchError(this.handleError),
                takeUntil(this.store.pipe(select(isAuthenticated), filter(isAuthenticated => !isAuthenticated)))
            );
    }

    /**
     * Retrieves list of requests for receiver
     * @param orgGuid
     * @param appGuid
     * @param institutionGuid
     * @param status, '' (empty string) - get all requests (PENDING, REJECTED, APPROVED)
     * @returns
     */
    getRequestListForRecipient(orgGuid: string, status?: string, appGuid?: string, institutionGuid?: string): Observable<DataAccessRequestModel[]> {
        let params = {};
        appGuid = appGuid != null && typeof appGuid !== 'undefined' ? appGuid : '';
        institutionGuid = institutionGuid != null && typeof institutionGuid !== 'undefined' ? institutionGuid : '';
        status = status != null && typeof status !== 'undefined' ? status : '';
        if (appGuid) {
            params = {'appGuid': appGuid, 'status': status};
        } else if (institutionGuid) {
            params = {'institutionGuid': institutionGuid, 'status': status};
        } else {
            params = {'status': status};
        }
        const url = `${environment.datahubBaseURI}/api/v2/applications/requests/recipient/${orgGuid}`;
        return this.http
            .get<any>(url, {params: params})
            .pipe(
                map((requests: DataAccessRequestModel[]) =>
                    this.trimRequestMessages(requests)
                ),
                catchError(this.handleError),
                takeUntil(this.store.pipe(select(isAuthenticated), filter(isAuthenticated => !isAuthenticated)))
            );
    }

    /**
     * Retrieves list of requests for receiver
     * @param vendorOrgGuid
     * @param institutionOrgGuid
     * @param appGuid
     * @param status, '' (empty string) - get all requests (PENDING, REJECTED, APPROVED)
     * @returns
     */
    getRequestListBetweenVendorAndDistrict(vendorOrgGuid: string, institutionOrgGuid: string, status?: string, appGuid?: string): Observable<DataAccessRequestModel[]> {
        let params = {};
        appGuid = appGuid != null && typeof appGuid !== 'undefined' ? appGuid : '';
        status = status != null && typeof status !== 'undefined' ? status : '';
        if (appGuid) {
            params = {'appGuid': appGuid, 'status': status};
        } else {
            params = {'status': status};
        }
        const url = `${environment.datahubBaseURI}/api/v2/applications/requests/between/vendor/${vendorOrgGuid}/and/institution/${institutionOrgGuid}`;
        return this.http
            .get<any>(url, {params: params})
            .pipe(
                map((requests: DataAccessRequestModel[]) =>
                    this.trimRequestMessages(requests)
                ),
                catchError(this.handleError),
                takeUntil(this.store.pipe(select(isAuthenticated), filter(isAuthenticated => !isAuthenticated)))
            );
    }

    getOrgsWithApprovedRequests(): Observable<string[]> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/requests/approved`;
        return this.http
            .get<any>(url)
            .pipe(catchError(this.handleError));
    }

    getRosterAccessProgress(integrationId: string, districtId: string): Observable<number> {
        return this.http
            .get<number>(`${environment.datahubBaseURI}/api/v2/rosterAccess/progress/${integrationId}/${districtId}`);
    }

    /**
     * Retrieves rules for tasks of integration
     * @param integrationId
     * @param taskId
     * @returns
     */
    getRules(integrationId: string): Observable<any> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/integrations/${integrationId}/rules`;
        return this.http
            .get<any>(url)
            .pipe(catchError(this.handleError));
    }

    getRuleValueSuggestions(integrationId: string, taskId: string, propertyName: string, query: string): Observable<SuggestionModel[]> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/integrations/${integrationId}/tasks/${taskId}` +
        `/filters/suggestions?propertyName=${encodeURIComponent(propertyName)}&query=${encodeURIComponent(query)}`;
        return this.http
            .get<SuggestionModel[]>(url)
            .pipe(catchError(this.handleError));
    }

    loadAllRulesForIntegration(integrationId: string, integration: Integration): Observable<RulesAndTransforms> {
        if (integration.tasks) {
            return this.getRules(integrationId).pipe(
                map(rules => {
                    const internal = IntegrationDomain.getInternal(integration);
                    const result = {rules: [], transforms: []};
                    Object.keys(rules).forEach((taskId: string) => {
                        const task: IntegrationTask = integration.tasks.find((item: IntegrationTask) => item.taskId === taskId);

                        rules[taskId].filtrations.forEach((filter: RequestedFilterModel) => {
                            filter.isMyFilter = false;
                            if (filter.linkedEntityTypes && filter.linkedEntityTypes.length > 1) {
                                filter.linkedEntityTypes.sort((linkedEntityA: string, linkedEntityB: string) =>
                                    (INTEGRATION_TASKS_ORDER[linkedEntityA] || 100) - (INTEGRATION_TASKS_ORDER[linkedEntityB] || 100)
                                );
                            }

                            if (task) {
                                IntegrationService.processFilterWithRelatedTask(filter, task, internal, integration);
                            }

                            RequestedFilterViewModel.processFilterWithSchoolId(<RequestedFilterViewModel>filter);
                        });

                        if (task) {
                            task.validations = rules[taskId].validations;
                        }

                        result.rules.push(...rules[taskId].filtrations);
                        result.transforms.push(...rules[taskId].transformations);
                    });

                    if (result.rules) {
                        RequestedFilterViewModel.sortFiltrations(result.rules);

                        result.rules = result.rules.map((filter: RequestedFilterViewModel, filterIndex: number) => {
                            filter.filterIndex = filterIndex;
                            return filter;
                        });
                    }

                    return result;
                }));
        } else {
            return observableOf(new RulesAndTransforms());
        }
    }

    getDistrictSchools(districtGuid: string, applicationGuid: string) {
        let url = `${environment.datahubBaseURI}/api/v2/institutions/districts/${districtGuid}/schools`;
        if (applicationGuid) {
            url += `?application=${applicationGuid}`;
        }
        return this.http
            .get<SchoolProfileModel[]>(url)
            .pipe(
                map(items => items.map(item => {
                    item.status = item.syncStatus; return item;
                })),
                catchError(this.handleError)
            );
    }

    getApplicationProfile(applicationId): Observable<Application> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/${applicationId}`;
        return this.http
            .get<Application>(url)
            .pipe(catchError(this.handleError));
    }

    getIntegrationCompleteByIntegr(integrationGuid: string, providerGuid: string): Observable<IntegrationComplete> {
        return this.getIntegrationById(integrationGuid)
            .pipe(
                mergeMap((cInt: Integration) => {
                    if (cInt.applicationId) {
                        const cInt$ = observableOf(cInt);
                        const dInt$ = this.getDefaultIntegration(cInt.applicationId);
                        const app$ = this.getApplicationProfile(cInt.applicationId);
                        const sensitiveData$ = this.getSensitiveData(providerGuid);

                        return this.getIntegrationComplete(dInt$, cInt$, app$, cInt.applicationId, providerGuid, sensitiveData$);
                    } else {
                        throw new Error('Application is not exists');
                    }
                })
            );
    }
    getIntegrationCompleteByApp(applicationGuid: string, providerGuid: string): Observable<IntegrationComplete> {
        if (applicationGuid) {
            const cInt$ = this.getIntegration(applicationGuid, providerGuid)
                .pipe(catchError(err => observableOf(null)));
            const dInt$ = this.getDefaultIntegration(applicationGuid);
            const app$ = this.getApplicationProfile(applicationGuid);
            const sensitiveData$ = this.getSensitiveData(providerGuid);

            return this.getIntegrationComplete(dInt$, cInt$, app$, applicationGuid, providerGuid, sensitiveData$);
        } else {
            throw new Error('Application is not exists');
        }
    }

    private getIntegrationComplete(
        dInt$, cInt$, app$, applicationGuid: string, providerGuid: string, sensitiveDataList$?
    ): Observable<IntegrationComplete> {
        return forkJoin(dInt$, cInt$, app$, sensitiveDataList$ ? sensitiveDataList$ : of([]))
            .pipe(
                concatMap(([dInt, cInt, app, sensitiveDataList]:
                               [Integration, Integration, Application, SensitiveData[]]) => {
                    let integrationIdForRules = cInt ? cInt.integrationId : dInt.integrationId;
                    if (cInt && [IntegrationState.PENDING, IntegrationState.REJECTED].indexOf(cInt.state) > -1) {
                        cInt.tasks = Object.assign([], dInt.tasks);
                        integrationIdForRules = dInt.integrationId;
                    }
                    const baseIntegration = cInt ? cInt : dInt;

                    const sensitiveDataObj = {}; // { [sensitiveData.propertyName + sensitiveData.type] : SensitiveData }
                    if (sensitiveDataList) {
                        sensitiveDataList.forEach((sensitiveData: SensitiveData) => {
                            sensitiveDataObj[sensitiveData.propertyName + sensitiveData.type] = sensitiveData;
                        });
                    }

                    const piiLevel$ = cInt
                        ? this.piiShieldService.getIntegrationPIILevel(integrationIdForRules)
                        : this.piiShieldService.getApplicationPIILevel(app.guid);

                    return forkJoin(
                        observableOf(dInt),
                        observableOf(baseIntegration),
                        // this.getDistrictSchools(providerGuid, applicationGuid),
                        this.orgsService.getSchoolsOfDistrict(providerGuid, SchoolFilterType.ALL, applicationGuid, '', 10000),
                        this.loadAllRulesForIntegration(integrationIdForRules, baseIntegration),
                        this.getPredicatesMetadata(FilterDefaultPredicateMap),
                        observableOf(app),
                        observableOf(sensitiveDataObj),
                        piiLevel$
                    );
                }),
                mergeMap((
                    [defaultIntegration, currentIntegration, schools, filtersMetadata, predicatesMetadata, application, sensitiveDataObj, piiLevel]:
                        [Integration, Integration, SchoolProfileModel[], RulesAndTransforms, MetadataModel[], Application, object, PII_LEVEL]) => {
                    const ic = new IntegrationComplete();
                    ic.defaultIntegrationMetadata = defaultIntegration;
                    ic.currentIntegrationMetadata = currentIntegration;
                    ic.application = Object.assign({}, application, {piiLevel: piiLevel});
                    ic.schoolSet = new SchoolSet(schools, ic);
                    // ic.attrMetadata = predicatesMetadata;
                    ic.attrEntities = this.buildAttributeEntities(defaultIntegration, currentIntegration);
                    ic.filtersMetadata = filtersMetadata;
                    ic.filtersPredicatesMetadata = FiltersListService.filterDeniedPredicates(predicatesMetadata);
                    const rules = this.buildRulesOfFilters(currentIntegration, filtersMetadata, predicatesMetadata, ic.attrEntities);
                    ic.ruleSet = new RestrictionSet(rules);
                    ic.ssoRuleSet = new RestrictionSet([]);
                    ic.sensitiveDataObj = sensitiveDataObj;
                    return observableOf(ic);
                })
            );
    }

    buildAttributeEntitiesForRequest(ic: IntegrationComplete, request: DataAccessRequestModel): AttributeEntity[] {

        // current integration doesn't contain tasks bofore applying request
        // so we take default integration
        // for first request IntegrationComplete already exists tasks from /original
        const internal = IntegrationDomain.getInternal(ic.defaultIntegrationMetadata);
        // const external = IntegrationDomain.getExternal(ic.defaultIntegrationMetadata);
        // ic.attrEntities = this.buildAttributeEntities(ic.defaultIntegrationMetadata, null);

        request.requestedTasks.forEach(rTask => {

            ic.attrEntities.forEach((attrEntity: AttributeEntity) => {

                attrEntity.sameEntityList
                    .filter((am: AttributeEntity) => internal.equalsEntityToTask(am, rTask))
                    .forEach((entity: AttributeEntity) => {

                        entity.modifiedBy = rTask;

                        rTask.requestedAttributes.forEach((rAttr: RequestedAttributeModel) => {
                            entity.attributes
                                .filter((attr: AttributeEntityItem) => internal.equalsAttrsReq(rAttr, attr))
                                .forEach((attr: AttributeEntityItem) => {
                                    attr.modifiedBy = rAttr;
                                    attr.enabled = attr.enabled && rAttr.requestedAction !== RequestedAction.REMOVE;
                                });
                        });
                    });
            });
        });

        return ic.attrEntities;
    }

    buildAttributeEntities(originalIntegration: Integration, currentIntegration: Integration): AttributeEntity[] {
        const resp = new Array<AttributeEntity>();
        const internal = IntegrationDomain.getInternal(currentIntegration);
        const external = IntegrationDomain.getExternal(currentIntegration);

        if (currentIntegration.tasks) {
            const eduToneDS = (currentIntegration.source.dataSourceType === DataSourceType.EduTone) ? 'source' : 'target';

            currentIntegration.tasks.forEach((currentIntTask: IntegrationTask) => {

                const originalIntTask: IntegrationTask = originalIntegration.tasks
                    .find((task: IntegrationTask) =>
                        (currentIntTask.sourceType === task.sourceType) && (currentIntTask.targetType === task.targetType)
                    );

                const displayType = internal.displayTypeName(currentIntTask);
                const currentEntity = AttributeEntity.instanceFromIntegrationTask(currentIntTask);
                let entity = resp.find((entityItem: AttributeEntity) => entityItem.displayType === displayType);
                if (!entity) {
                    entity = currentEntity;

                    if (Object.keys(INTEGRATION_TASKS_ORDER).indexOf(internal.getTaskSourceType(currentIntTask)) > -1) {
                        entity.position = INTEGRATION_TASKS_ORDER[internal.getTaskSourceType(currentIntTask)];
                    }

                    resp.push(entity);
                } else {
                    entity.sameEntityList.push(currentEntity);
                    currentEntity.attributes = entity.attributes;
                }

                currentEntity.displayType = internal.displayTypeName(currentIntTask);
                currentEntity.displayExternalType = external.displayTypeName(currentIntTask);

                const skipIfMissingAttributes = IntegrationService.getValidationAttributesList(
                    currentIntTask.validations || [], FilterAction.VALIDATE_AND_SKIP
                );

                currentIntTask.mappings.forEach((mapping: IntegrationTaskMap) => {
                    const internalAttrDesc = internal.getTaskAttributes(currentIntTask)
                        .find((ad: DataAttribute) => internal.equalsAttrsMap(ad, mapping));
                    internal.setMappingProperty(mapping, internalAttrDesc);

                    const externalAttrDesc = external.getTaskAttributes(currentIntTask)
                        .find((ad: DataAttribute) => external.equalsAttrsMap(ad, mapping));
                    external.setMappingProperty(mapping, externalAttrDesc);
                });

                const mappings = currentIntTask.mappings;
                if (originalIntTask) {
                    originalIntTask.mappings.forEach((mapping: IntegrationTaskMap) => {
                        const existingMapping: IntegrationTaskMap = mappings
                            .find((mappingItem: IntegrationTaskMap) =>
                                (mapping.sourcePropertyName === mappingItem.sourcePropertyName)
                                && (mapping.targetPropertyName === mappingItem.targetPropertyName)
                                && (mapping.backToSourcePropertyName === mappingItem.backToSourcePropertyName)
                            );

                        if (!existingMapping) {
                            const internalAttrDesc = internal.getTaskAttributes(originalIntTask)
                                .find((ad: DataAttribute) => internal.equalsAttrsMap(ad, mapping));
                            internal.setMappingProperty(mapping, internalAttrDesc);

                            const externalAttrDesc = external.getTaskAttributes(originalIntTask)
                                .find((ad: DataAttribute) => external.equalsAttrsMap(ad, mapping));
                            external.setMappingProperty(mapping, externalAttrDesc);

                            mapping.taskId = currentIntTask.taskId;
                            mapping.enabled = false;
                            mappings.push(mapping);
                        }
                    });
                }

                mappings.forEach((mapping: IntegrationTaskMap) => {
                    const item = new AttributeEntityItem();
                    item.mappingId = mapping.mappingId;
                    item.enabled = mapping.enabled;
                    item.sourcePropertyName = mapping.sourcePropertyName;
                    item.targetPropertyName = mapping.targetPropertyName;
                    item.backToSourcePropertyName = mapping.backToSourcePropertyName;
                    item.taskId = mapping.taskId;
                    item.attrEntity = currentEntity;
                    item.sameMappedAttributes = [item];
                    item.piiShieldAction = (currentIntegration.state === IntegrationState.ORIGINAL) ? null : mapping.piiShieldAction;

                    const internalAttrDesc = internal.getMappingProperty(mapping);
                    const externalAttrDesc = external.getMappingProperty(mapping);

                    if (internalAttrDesc) {
                        item.domain = internalAttrDesc.domain;
                        item.entityTypeDenom = internalAttrDesc.entityTypeDenom;
                        item.propertyTypeDenom = internalAttrDesc.propertyTypeDenom;
                        item.propertyName = internalAttrDesc.propertyName;
                        item.friendlyName = internalAttrDesc.friendlyName;
                        if (externalAttrDesc) {
                            item.required = externalAttrDesc.required;
                        }
                        item.order = internalAttrDesc.order;
                    } else {
                        item.domain = internal.dataSourceType.toString();
                        item.entityTypeDenom = internal.getTaskSourceType(currentIntTask);
                        item.propertyTypeDenom = 'String';
                        item.propertyName = internal.getMappingAttrPropertyName(mapping);
                        item.friendlyName = internal.getMappingAttrPropertyName(mapping);
                    }

                    const attribute = entity.attributes.find((attr: AttributeEntityItem) =>
                        (attr[eduToneDS + 'PropertyName'] === mapping[eduToneDS + 'PropertyName'])
                    );
                    const skipIfMissing = (skipIfMissingAttributes.indexOf(item.propertyName) > -1);
                    item.skipIfMissing = item.skipIfMissing || skipIfMissing;

                    if (attribute) {
                        attribute.sameMappedAttributes.push(item);
                    } else {
                        entity.attributes.push(item);
                    }
                });
            });
        }
        return resp;
    }

    buildRulesOfFilters(
        integration: Integration,
        filtersMetadata: RulesAndTransforms,
        filtersPredicatesMetadata: MetadataModel[],
        attrEntities: AttributeEntity[]
    ): RestrictionRule[] {
        return filtersMetadata.rules.map(ruleMetadata => {

            // TODO add compare to ruleMetadata.ownerGuid

            const rule: RestrictionRule = new RestrictionRule();
            rule.entityFilterId = ruleMetadata.entityFilterId;

            ruleMetadata.propertyFilters.forEach( prop => {

                const row: RestrictionFilter = new RestrictionFilter(filtersPredicatesMetadata, integration.integrationId);
                attrEntities.forEach(entity => {

                    const attr: AttributeEntityItem = entity.attributes
                        .filter(a => (a.targetPropertyName === prop.propertyName) || (a.propertyName === prop.propertyName))[0]; // TODO clarify

                    if (attr) {
                        row.entity = new OneAttributeEntity(
                            integration.integrationId,
                            entity.taskId,
                            entity.sourceType,
                            entity.sourceType,
                            entity.edutoneEntity,
                            attr
                        );

                        const plist: MetadataModel = filtersPredicatesMetadata
                            .filter(it => it.type === attr.propertyTypeDenom)[0];
                        if (plist) {
                            row.predicate = plist.predicates.filter(p => p.name == prop.predicateName.toLocaleString())[0];
                        }

                        if (row.predicate && row.predicate.isSupportMultipleArgument) {
                            row.value = prop.arguments;
                        } else {
                            if (prop.arguments && (prop.arguments.length > 0)) {
                                row.value = prop.arguments[0].id;
                            }
                        }
                    }
                });

                if (row.isCompleted()) {
                    row.rule = rule;
                    rule.items.push(row);
                }
            });

            return rule;
        });
    }

    cloneIntegration(integration: Integration): Integration {
        const newIntegration: Integration = new Integration();
        newIntegration.integrationId = null;
        newIntegration.title = integration.title;
        newIntegration.source = Object.assign({}, integration.source);
        newIntegration.target = Object.assign({}, integration.target);
        newIntegration.mode = integration.mode;
        newIntegration.state = IntegrationState.PENDING;
        // newIntegration.expiredDate = integration.expiredDate;
        newIntegration.applicationId = integration.applicationId;
        return newIntegration;
    }

    getPredicatesMetadata(defaultPredicateMap: Map<AttributeType, FilterPredicate>): Observable<MetadataModel[]> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/filters/metadata`;
        return this.http
            .get<any>(url)
            .pipe(
                map((predicatesMetadata: MetadataModel[]) => {
                    predicatesMetadata.forEach((metadata: MetadataModel) => {
                        metadata.predicates.sort((a: PredicateModel, b: PredicateModel) =>
                            (FilterPredicateFriendlyName[a.name] || '')
                                .toLowerCase().localeCompare((FilterPredicateFriendlyName[b.name] || '').toLowerCase())
                        );
                        metadata.defaultPredicate = defaultPredicateMap.get(metadata.type);
                    });
                    return predicatesMetadata;
                }),
                catchError(this.handleError)
            );
    }

    getOrganizationsMapping(integrationGuid: string) {
        const url = `${environment.datahubBaseURI}/api/v2/applications/integrations/${integrationGuid}/organizations/mapping`;
        return this.http
            .get<OrganizationsMapping>(url)
            .pipe(catchError(this.handleError));
    }

    public convertExpirationDateToTimestamp(date: Date): string {
        const expirationDate = moment(date).toDate();
        const convertedDate = moment(Date.UTC(expirationDate.getFullYear(), expirationDate.getMonth(), expirationDate.getDate())).toDate();
        convertedDate.setHours(23, 59, 59);
        return convertedDate.toISOString();
    }

    runSync(integrationId: string, type: string, isPreviewModeExport?: boolean, deleteAllData?: boolean, entityTypesForDelete: string[] = []): any {
        const previewModeParam = isPreviewModeExport ? `?isPreviewModeExport=${isPreviewModeExport}` : '';
        const deleteAllDataParam = deleteAllData
            ? `${isPreviewModeExport ? '&' : '?'}`
              + ((entityTypesForDelete?.length > 0) ? `entityTypesForDelete=${entityTypesForDelete.join()}` : '')
            : '';
        const url = `${environment.datahubBaseURI}/api/v2/applications/integrations/${integrationId}/executions/${type}${previewModeParam}${deleteAllDataParam}`;

        return this.http.post(url, '', {responseType: 'text' as const})
            .pipe(catchError(this.handleError));
    }

    suspendSync(integrationId: string): Observable<any> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/integrations/${integrationId}/suspend`;

        return this.http.post(url, null)
            .pipe(catchError(this.handleError));
    }

    unsuspendSync(integrationId: string): Observable<any> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/integrations/${integrationId}/unsuspend`;

        return this.http.post(url, null)
            .pipe(catchError(this.handleError));
    }

    getSensitiveData(orgGuid: string): Observable<SensitiveData[]> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/pii/orgs/${orgGuid}/sensitive_fields`;

        return this.http.get<SensitiveData[]>(url)
            .pipe(catchError(this.handleError));
    }

    saveSensitiveData(orgGuid: string, sensitiveDataList: SensitiveData[]): Observable<SensitiveData[]> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/pii/orgs/${orgGuid}/sensitive_fields`;

        return this.http.put<SensitiveData[]>(url, sensitiveDataList)
            .pipe(catchError(this.handleError));
    }

    getAllEntitiesRequiredAttributes(attrEntities: AttributeEntity[], enabled = false): AttributeEntityItem[] {
        if (attrEntities?.length == 0) {
            return [];
        }
        return attrEntities.reduce(
            (attributes: AttributeEntityItem[], entity: AttributeEntity) => {
                return attributes.filter((attr: AttributeEntityItem) =>
                    entity.attributes.some((entityAttr: AttributeEntityItem) =>
                        (entityAttr.enabled || enabled) && entityAttr.required && (attr.propertyName === entityAttr.propertyName)
                    ) || (
                        entity.sourceType === 'School' && attr.propertyName.startsWith('sisIds[')
                    )
                );
            },
            attrEntities?.length > 1 ? attrEntities[1].attributes : attrEntities[0].attributes
        );
    }

    getRequestAllEntitiesRequiredAttributes(requestedTasks: RequestedTaskModel[]): RequestedAttributeModel[] {
        if (requestedTasks?.length == 0) {
            return [];
        }
        return requestedTasks.reduce(
            (attributes: RequestedAttributeModel[], entity: RequestedTaskModel) => {
                return attributes.filter((attr: RequestedAttributeModel) =>
                    entity.requestedAttributes.some((entityAttr: RequestedAttributeModel) =>
                        (entityAttr.requestedAction === RequestedAction.ADD) && (attr.sourcePropertyName === entityAttr.sourcePropertyName)
                    ) || (
                        entity.sourceType === 'School' && attr.sourcePropertyName.startsWith('sisIds[')
                    )
                );
            },
            requestedTasks?.length > 1 ? requestedTasks[1].requestedAttributes : requestedTasks[0].requestedAttributes
        );
    }

    excludeAttributes(attributes: AttributeEntityItem[], exclude: AttributeEntityItem[]): AttributeEntityItem[] {
        return attributes.filter((attr: AttributeEntityItem) =>
            exclude.every((excludeAttr: AttributeEntityItem) =>
                attr.propertyName !== excludeAttr.propertyName
            )
        );
    }

    public logError(errorMsg: string) {
        this.logService.logError(errorMsg);
    }

    private getQueryParams(params: Object): string {
        return Object.keys(params)
            .filter((key: string) => params[key])
            .map((key: string) => `${key}=${params[key]}`)
            .join('&');
    }

    private handleError(error: HttpErrorResponse) {
        if (error.error instanceof ErrorEvent) {
            console.error('An error occurred:', error.error.message);
        } else {
            console.error(
                `Backend returned ${error.status}, ${error.error}`);
        }
        return throwError(
            'Something bad happened, please try again later.');
    }

    private trimRequestMessages(requests: DataAccessRequestModel[]): DataAccessRequestModel[] {
        return requests.map((request: DataAccessRequestModel)=> {
            request.requestMessage = request.requestMessage ? request.requestMessage.trim() : request.requestMessage;
            request.replyMessage = request.replyMessage ? request.replyMessage.trim() : request.replyMessage;
            return request;
        });
    }
}

export class IntegrationFormData {

    integrationVendor: IntegrationVendor;
    integrationProvider: IntegrationProvider;
    outRequest: IntegrationChangesObserver;
    _direction: string;
    _isIntegrationCorrupted: boolean;
    options = new IntegrationFormOptions();
    requestFiles: DataAccessRequestFileLightModel[] | any[];
    activatedRolesForSchools: RequestedSchoolRoles;
    private readonly maxDate = new Date(2099, 11, 30);

    static isRolesChanged(request: DataAccessRequestModel): boolean {
        let isRolesChanged = false;
        if (request.activatedRolesForSchools) {
            for (const schoolGuid in request.activatedRolesForSchools) {
                if (!isRolesChanged
                    && request.activatedRolesForSchools.hasOwnProperty(schoolGuid)
                    && schoolGuid !== 'requestedAction') {
                    isRolesChanged = !!request.activatedRolesForSchools.requestedAction
                        && request.activatedRolesForSchools[schoolGuid].some((schoolRole: SchoolRole) => !!schoolRole.requestedAction);
                }
            }
        }
        return isRolesChanged;
    }

    constructor(
        private integrationService: IntegrationService,
        public ic: IntegrationComplete
        // private dataBrowser: DataBrowser,
    ) {
        this.outRequest = new IntegrationChangesObserver(this.ic.currentIntegrationMetadata);
        this.checkIsIntegrationCorrupted();

        this.integrationService.onChangeIntegration
            .subscribe({
                next: (event: IntegrationChangeEvent) => this.outRequest.handlerIntegrationChanges(event)
            });

        // this.dataBrowser.onChangeFilters
        //     .subscribe({
        //         next: (event: FilteringChangeEvent) => this.outRequest.handlerFiltersChanges(event)
        //     });
    }

    vendor(vendor: IntegrationVendor) {
        this.integrationVendor = vendor;
        this.outRequest.builder.vendorName(vendor.name);
        return this;
    }

    provider(provider: IntegrationProvider) {
        this.integrationProvider = provider;
        this.outRequest.builder.providerName(provider.name);
        return this;
    }

    getSender(): Counterparty {
        if (this._direction === 'V-P') {
            return this.integrationVendor;
        } else if (this._direction === 'P-V') {
            return this.integrationProvider;
        }
    }

    getReceiver(): Counterparty {
        if (this._direction === 'V-P') {
            return this.integrationProvider;
        } else if (this._direction === 'P-V') {
            return this.integrationVendor;
        }
    }

    requestedFilters(requestedFilters: RequestedFilterModel[]): IntegrationFormData {
        requestedFilters.forEach((filter: RequestedFilterModel) => {
            RequestedFilterViewModel.processFilterWithSchoolId(<RequestedFilterViewModel>filter);

            if (!filter.eduToneEntityType) {
                filter.eduToneEntityType = filter.taskId.split('-')[0];
            }
        });
        this.outRequest.builder.requestedFilters(requestedFilters);
        return this;
    }

    direction(dir: string) {
        this._direction = dir;
        if (this._direction === 'V-P') {
            this.outRequest.builder.sender(this.integrationVendor.guid);
            this.outRequest.builder.recipient(this.integrationProvider.guid);
        } else if (this._direction === 'P-V') {
            this.outRequest.builder.sender(this.integrationProvider.guid);
            this.outRequest.builder.recipient(this.integrationVendor.guid);
        }
        return this;
    }

    files(files: DataAccessRequestFileLightModel[]) {
        this.requestFiles = files;
        this.outRequest.builder.files(files);
        return this;
    }

    requestedSchoolRoles(activatedRolesForSchools: RequestedSchoolRoles) {
        this.activatedRolesForSchools = activatedRolesForSchools;
        this.outRequest.builder.activatedRolesForSchools(activatedRolesForSchools);
        return this;
    }

    /**
     * Prepare for send data access request and send activation
     */
    private initializationForInvite(oneSelectedSchool?: SchoolProfileModel) {
        if (!this.ic.currentIntegrationMetadata) {
            return;
        }

        const isIntegrationActive = this.isActiveStatus(this.ic.currentIntegrationMetadata.state);
        this.options.mode = IntegrationFormMode.INVITE;
        this.options.visibleMessage = false;
        this.options.editableAttributes = <FEATURE_TYPES>this.ic.application.featureType !== FEATURE_TYPES.SSO;
        this.options.visibleFilters = isFiltersAllowedForDataSource(this.ic.application.dataSourceType)
            && <FEATURE_TYPES>this.ic.application.featureType !== FEATURE_TYPES.SSO;
        this.options.editableFilters = this.options.visibleFilters;
        this.options.visibleSsoFilters = isFiltersAllowedForDataSource(this.ic.application.dataSourceType)
            && Application.isSupportSSO(this.ic.application);
        this.options.editableSsoFilters = this.options.visibleSsoFilters && this._direction === 'P-V';
        this.options.editableAttachment = <FEATURE_TYPES>this.ic.application.featureType !== FEATURE_TYPES.SSO;
        this.options.visibleExpirationDate = (!isIntegrationActive || this.ic.isExpiredStatus()) && this._direction === 'P-V'
            && <FEATURE_TYPES>this.ic.application.featureType !== FEATURE_TYPES.SSO;
        this.options.editableExpirationDate = (!isIntegrationActive || this.ic.isExpiredStatus()) && this._direction === 'P-V'
            && this.options.visibleExpirationDate;
        this.options.visibleAppAssignmentMode = this.options.visibleFilters && this._direction === 'P-V'
            && Application.isSupportSSO(this.ic.application);
        this.options.editableAppAssignmentMode = this.options.visibleAppAssignmentMode;
        this.options.visibleDataSourceConfig = !isIntegrationActive && this._direction === 'V-P'
            && <FEATURE_TYPES>this.ic.application.featureType !== FEATURE_TYPES.SSO;

        // include all schools
        if (this.ic.schoolSet) {

            if (this.integrationProvider.independent) { // independent school
                this.options.mode = IntegrationFormMode.INVITE_INDEPENDENT;
                // don't show schools
                this.options.editableSchools = false;
                this.options.visibleSchools = false;
                this.outRequest.builder.add(
                    this.outRequest.builder.buildSchool(this.integrationProvider.model as SchoolProfileModel)
                );

            } else {

                if (oneSelectedSchool) { // one school of district

                    // selected only one school
                    this.options.visibleSchools = true;
                    this.options.editableSchools = true;

                    this.ic.schoolSet.oneSelectedSchoolGuid = oneSelectedSchool.schoolGuid;
                    this.ic.schoolSet.schools.forEach((school: SchoolProfileModel) => {
                        if (school.schoolGuid === oneSelectedSchool.schoolGuid) {
                            school.checked = true;
                            this.outRequest.builder.add(this.outRequest.builder.buildSchool(school));
                        } else {
                            school.checked = this.isActiveStatus(school.syncStatus);
                        }
                    });

                } else { // district with some schools

                    this.options.visibleSchools = true;
                    this.ic.schoolSet.schools.forEach(obj => {
                        if (!this.isActiveStatus(obj.status)) {
                            obj.checked = true;
                            this.outRequest.builder.add(this.outRequest.builder.buildSchool(obj));
                        }
                    });
                }
            }
        }

        // fills attributes
        if (this.ic.attrEntities && !isIntegrationActive) {
            this.ic.attrEntities.forEach((attrEntity: AttributeEntity) => {
                attrEntity.attributes.forEach((attributeItem: AttributeEntityItem) => {
                    attributeItem.sameMappedAttributes.forEach((attr: AttributeEntityItem) => {
                        if (attr.enabled) {
                            this.outRequest.builder.add(
                                this.outRequest.builder.buildTaskAttr(attr),
                                this.outRequest.builder.buildTask(attr.attrEntity));
                        }
                    });
                });


                if ((this.ic.application.featureType === FEATURE_TYPES.SSO) && (attrEntity.attributes.length === 0)) {
                    this.outRequest.builder.add(this.outRequest.builder.buildTask(attrEntity));
                }
            });
        }

        // include all filters
        // if (this.ic.ruleSet) {
        //     this.ic.ruleSet.rules.forEach(rule => {
        //         const rb = this.outRequest.builder.buildRule(rule);
        //         this.outRequest.builder.add(rb);
        //
        //         rule.items.forEach(filter => {
        //             const fb = this.outRequest.builder.buildFilter(filter);
        //             this.outRequest.builder.add(fb, rb);
        //
        //             if (filter.predicate.isSupportMultipleArgument) {
        //                 filter.value.forEach(v => this.outRequest.builder.add(this.outRequest.builder.buildFilterVal(v), fb));
        //             } else {
        //                 const arg = new SuggestionModel();
        //                 arg.id = arg.name = filter.value;
        //                 this.outRequest.builder.add(this.outRequest.builder.buildFilterVal(arg), fb);
        //             }
        //
        //         });
        //     });
        // }

        console.log(this.outRequest.builder.build());
    }

    /**
     * Prepare for overview integration
     */
    private initializationForOverview(oneSelectedSchool?: SchoolProfileModel) {

        this.options.visibleMessage = true;
        this.options.visibleFilters = isFiltersAllowedForDataSource(this.ic.application.dataSourceType)
            && <FEATURE_TYPES>this.ic.application.featureType !== FEATURE_TYPES.SSO;
        this.options.editableFilters = this.options.visibleFilters;
        this.options.visibleSsoFilters = false;
        this.options.editableSsoFilters = false;
        this.options.visibleAttributes = <FEATURE_TYPES>this.ic.application.featureType !== FEATURE_TYPES.SSO;
        this.options.editableAttachment = <FEATURE_TYPES>this.ic.application.featureType !== FEATURE_TYPES.SSO;
        this.options.visibleDataBrowsing = <FEATURE_TYPES>this.ic.application.featureType !== FEATURE_TYPES.SSO;
        this.options.visibleExpirationDate = this.ic.isExpiredStatus() && this._direction === 'P-V'
            && <FEATURE_TYPES>this.ic.application.featureType !== FEATURE_TYPES.SSO;
        this.options.editableExpirationDate = this.ic.isExpiredStatus() && this._direction === 'P-V';
        this.options.visibleAppAssignmentMode = this.options.visibleFilters && this._direction === 'P-V'
            && Application.isSupportSSO(this.ic.application);
        this.options.editableAppAssignmentMode = this.options.visibleAppAssignmentMode;

        if (this.ic.isActiveStatus()) {

            if (this.integrationProvider.independent) {
                this.options.mode = IntegrationFormMode.OVERVIEW_INDEPENDENT;
                this.options.visibleSchools = false;
                this.options.editableSchools = false;

            } else {
                this.ic.schoolSet.oneSelectedSchoolGuid = (oneSelectedSchool && oneSelectedSchool.schoolGuid) ? oneSelectedSchool.schoolGuid : null;
                this.options.mode = IntegrationFormMode.OVERVIEW;
                this.options.visibleSchools = true;
                this.options.editableSchools = true;

                this.ic.schoolSet.schools
                    .filter(s => s.status === IntegrationState.SYNCED)
                    .forEach(obj => {
                        obj.checked = true;
                    });
            }

        } else {
            // TODO error
        }
    }

    initialization(oneSelectedSchool?: SchoolProfileModel) {
        if (this.ic.isActiveStatus()) {
            if (oneSelectedSchool && oneSelectedSchool.syncStatus !== IntegrationState.SYNCED) {
                this.initializationForInvite(oneSelectedSchool);
            } else {
                this.initializationForOverview(oneSelectedSchool);
            }
        } else {
            this.initializationForInvite(oneSelectedSchool);
        }
    }

    /**
     * Prepare for request overview
     */
    initializationForRequestOverview(request: DataAccessRequestModel, isCanApprove: boolean) {

        this.options.mode = ((request.status === DataAccessRequestStatus.PENDING) && isCanApprove)
            ? IntegrationFormMode.OVERVIEW_PENDING_REQUEST
            : IntegrationFormMode.OVERVIEW_REQUEST;
        this.options.setAllReadOnly();
        this.options.visibleMessage = true;
        this.options.visibleFilters = isFiltersAllowedForDataSource(this.ic.application.dataSourceType)
            && <FEATURE_TYPES>this.ic.application.featureType !== FEATURE_TYPES.SSO;
        this.options.visibleSsoFilters = isFiltersAllowedForDataSource(this.ic.application.dataSourceType)
            && Application.isSupportSSO(this.ic.application);

        const isRequestEmpty = (!request.requestedOrganizations || request.requestedOrganizations.length === 0) &&
                               (!request.requestedTasks || request.requestedTasks.length === 0) &&
                               (!request.requestedFilters || request.requestedFilters.length === 0);
        const isProviderAbleToSetExpirationTime = (this.options.mode === IntegrationFormMode.OVERVIEW_PENDING_REQUEST) &&
                                                  this._direction === 'V-P' && isCanApprove;
        const isIntegrationActive = this.ic.isActiveStatus();

        this.options.visibleExpirationDate = (request.expirationTs && request.expirationTs !== null ||
            !isIntegrationActive && isProviderAbleToSetExpirationTime ||
            this.ic.isExpiredStatus() && isProviderAbleToSetExpirationTime ||
            isRequestEmpty && isIntegrationActive && isCanApprove) &&
            <FEATURE_TYPES>this.ic.application.featureType !== FEATURE_TYPES.SSO;
        this.options.visibleAttributes = <FEATURE_TYPES>this.ic.application.featureType !== FEATURE_TYPES.SSO;
        this.options.editableFilters = isCanApprove && this._direction === 'V-P' && this.options.visibleFilters;
        this.options.editableSsoFilters = false;
        this.options.editableAttachment = isCanApprove && <FEATURE_TYPES>this.ic.application.featureType !== FEATURE_TYPES.SSO;
        this.options.visibleDataSourceConfig = !isIntegrationActive && isCanApprove && this._direction === 'P-V'
            && <FEATURE_TYPES>this.ic.application.featureType !== FEATURE_TYPES.SSO;

        if (request) {
            this.ic.schoolSet.schools.forEach(school => school.checked = (school.status === IntegrationState.SYNCED));

            if (request.requestedOrganizations) {
                request.requestedOrganizations.forEach(org => {
                    if (org.edutone) {
                        const schoolIndex = this.ic.schoolSet.schools.findIndex((school: SchoolProfileModel) => school.schoolGuid === org.edutone.identifier);

                        if (schoolIndex > -1) {
                            this.ic.schoolSet.schools[schoolIndex].modifiedBy = org;
                            this.ic.schoolSet.schools[schoolIndex].checked = org.requestedAction !== RequestedAction.REMOVE;
                        } else {
                            const missingSchool = new SchoolProfileModel();
                            missingSchool.schoolGuid = org.edutone.identifier;
                            missingSchool.schoolName = org.edutone.name;
                            missingSchool.modifiedBy = org;
                            missingSchool.checked = org.requestedAction !== RequestedAction.REMOVE;
                            this.ic.schoolSet.schools.push(missingSchool);
                        }
                    }
                });
            }
            if (request.requestedTasks) {
                this.integrationService.buildAttributeEntitiesForRequest(this.ic, request);
            }
            if (request.requestedFilters) {
                request.requestedFilters.forEach(fFilter => {
                    let f = false;
                    this.ic.ruleSet.rules.forEach(filter => {
                        if (filter.entityFilterId === fFilter.entityFilterId) {
                            f = true;
                            filter.modifiedBy = fFilter;
                        }
                    });
                    if (!f) {
                        // new filter
                        // TODO create and add new filter. how to add a filter without a rule?
                    }
                });
            }
            if (request.ssoRequestedFilters) {
                request.ssoRequestedFilters.forEach(fFilter => {
                    let f = false;
                    this.ic.ssoRuleSet.rules.forEach(filter => {
                        if (filter.entityFilterId === fFilter.entityFilterId) {
                            f = true;
                            filter.modifiedBy = fFilter;
                        }
                    });
                    if (!f) {
                        // new filter
                        // TODO create and add new filter. how to add a filter without a rule?
                    }
                });
            }
        }
    }

    buildRequest() {
        return this.outRequest.builder.forIntegration(this.ic.currentIntegrationMetadata).build();
    }

    needCreateIntegration(): boolean {
        return this.ic.currentIntegrationMetadata.integrationId === this.ic.defaultIntegrationMetadata.integrationId;
    }

    buildAndSendRequest(): Observable<DataAccessRequestModel> {
        const model: IntegrationWithRequestModel = new IntegrationWithRequestModel();

        if (this.needCreateIntegration()) {
            model.integrationModel = this.integrationService.cloneIntegration(this.ic.defaultIntegrationMetadata);
        }

        model.requestModel = this.buildRequest();
        this.deleteRequestedActionFromActivatedRoles(model.requestModel.activatedRolesForSchools);

        const files = model.requestModel.files;
        if (!files || (files?.length === 0)) {
            return this.integrationService.sendRequestWithIntegration(model, this.integrationProvider.guid)
                .pipe(
                    map((response: IntegrationWithRequestModel) => {
                        if (response.integrationModel) {
                            this.ic.currentIntegrationMetadata = response.integrationModel;
                        }
                        return response.requestModel;
                    })
                );
        }

        delete model.requestModel.files;
        let storedModel: IntegrationWithRequestModel;

        return this.integrationService.sendRequestWithIntegration(model, this.integrationProvider.guid)
            .pipe(
                switchMap((response: IntegrationWithRequestModel) => {
                    storedModel = response;
                    return forkJoin([this.integrationService.uploadFilesAfterRequest(storedModel, this.getSender().guid, files)])
                        .pipe(concatAll());
                }),
                map((response: HttpResponse<DataAccessRequestFileLightModel[]>) => <DataAccessRequestFileLightModel[]>response.body),
                map((storedFiles: DataAccessRequestFileLightModel[]) => {
                    if (storedModel.integrationModel) {
                        this.ic.currentIntegrationMetadata = storedModel.integrationModel;
                    }
                    if (storedFiles) {
                        storedModel.requestModel.files = storedFiles;
                    }
                    return storedModel.requestModel;
                })
            );
    }

    private deleteRequestedActionFromActivatedRoles(activatedRolesForSchools: RequestedSchoolRoles) {
        if (activatedRolesForSchools) {
            delete activatedRolesForSchools.requestedAction;
        }

        if (activatedRolesForSchools) {
            for (const schoolGuid in activatedRolesForSchools) {
                if (activatedRolesForSchools.hasOwnProperty(schoolGuid)
                    && activatedRolesForSchools[schoolGuid]) {

                    activatedRolesForSchools[schoolGuid].forEach((schoolRole: SchoolRole) => {
                        if (schoolRole.school) {
                            delete schoolRole.school;
                        }
                    });
                }
            }
        }
    }

    hasRequestedData(): boolean {
        const rr = this.outRequest.builder.request;
        const isRolesChanged = IntegrationFormData.isRolesChanged(rr) ? 1 : 0;
        return (isRolesChanged + rr.requestedFilters.length + rr.requestedOrganizations.length + rr.requestedTasks.length > 0);
    }

    isValid(filtersListFormGroup: UntypedFormGroup, ssoFiltersListFormGroup: UntypedFormGroup = null) {
        const request: DataAccessRequestModel = this.outRequest.builder.request;

        const isFiltersChanged = request.requestedFilters &&
            request.requestedFilters.some((filter: RequestedFilterModel) => filter.isMyFilter && !!filter.requestedAction);
        const isSsoFiltersChanged = request.ssoRequestedFilters &&
            request.ssoRequestedFilters.some((filter: RequestedFilterModel) => filter.isMyFilter && !!filter.requestedAction);
        const isAttributesChanged = request.requestedTasks
            .filter((task: RequestedTaskModel) => task.requestedAttributes.length > 0)
            .length > 0;
        const isRolesChanged = IntegrationFormData.isRolesChanged(request);
        const isOrgsChanged = request.requestedOrganizations.some((org: RequestedOrganizationModel) => org.requestedAction !== RequestedAction.UPDATE);
        const someOrgExists = this.ic.schoolSet.schools.some((school: SchoolProfileModel) => school.checked);

        const hasChanges = isOrgsChanged || isAttributesChanged || isFiltersChanged || isSsoFiltersChanged || isRolesChanged;
        const isFiltersValid = !filtersListFormGroup
            || filtersListFormGroup.valid
            || (<FEATURE_TYPES>this.ic.application.featureType === FEATURE_TYPES.SSO);
        const isSsoFiltersValid = !ssoFiltersListFormGroup
            || ssoFiltersListFormGroup.valid
            || Application.isSupportSSO(this.ic.application);
        const isExpirationDateValid = (!this.options.editableExpirationDate
            || (this.options.editableExpirationDate || request.expirationTs && request.expirationTs !== null)
            && moment(request.expirationTs).isAfter(new Date()) && moment(request.expirationTs).isBefore(this.maxDate))
            || (<FEATURE_TYPES>this.ic.application.featureType === FEATURE_TYPES.SSO);
        const isActive = this.isActiveStatus(this.ic.currentIntegrationMetadata.state);

        if (this.options.mode === IntegrationFormMode.INVITE) {
            return !this._isIntegrationCorrupted && hasChanges && (isOrgsChanged || isActive) && isFiltersValid && isSsoFiltersValid && isExpirationDateValid;

        } else if (this.options.mode === IntegrationFormMode.OVERVIEW) {
            return hasChanges && isFiltersValid && isExpirationDateValid && (someOrgExists || isOrgsChanged);

        } else if (this.options.mode === IntegrationFormMode.INVITE_INDEPENDENT) {
            return !this._isIntegrationCorrupted && hasChanges && isOrgsChanged && isFiltersValid && isExpirationDateValid;

        } else if (IntegrationFormMode.OVERVIEW_INDEPENDENT && isExpirationDateValid) {
            return hasChanges && isFiltersValid;

        } else if (this.options.mode === IntegrationFormMode.OVERVIEW_PENDING_REQUEST && isExpirationDateValid) {
            return isFiltersValid;
        } else if (this.options.mode === IntegrationFormMode.OVERVIEW_REQUEST && isExpirationDateValid) {
            return true;
        }
    }

    public isIntegrationCorrupted(): boolean {
        return this._isIntegrationCorrupted;
    }

    public checkIsIntegrationCorrupted() {
        this._isIntegrationCorrupted = [IntegrationFormMode.INVITE, IntegrationFormMode.INVITE_INDEPENDENT].includes(this.options.mode)
            && this.ic.currentIntegrationMetadata
            && this.ic.currentIntegrationMetadata.tasks
            && this.ic.currentIntegrationMetadata.tasks.some((task: IntegrationTask) =>
                task && (!task.mappings || task.mappings.length == 0 || !task.mappings.some((mapping: IntegrationTaskMap) => mapping && mapping.enabled))
            )
            && this.ic.application.featureType !== FEATURE_TYPES.SSO;

        if (this._isIntegrationCorrupted) {
            const errorMsg = `The attributes mapping is corrupted for an integration - ${this.ic.currentIntegrationMetadata.integrationId}`;
            this.integrationService.logError(errorMsg);
            console.error(errorMsg);
        }
    }

    private isActiveStatus(status: IntegrationState): boolean {
        return ACTIVE_INTEGRATION_STATES.indexOf(status) > -1;
    }
}

export class IntegrationChangesObserver {

    builder: DataAccessRequestBuilder;

    constructor(
        private integration: Integration
    ) {
        this.builder = new DataAccessRequestBuilder(this.integration);
    }

    handlerIntegrationChanges(event: IntegrationChangeEvent) {
        let rTask: RequestedTaskModel, rAttr: RequestedAttributeModel, rRule: RequestedFilterModel;
        let rSchool: RequestedOrganizationModel, rFilter: RequestedPropertyModel;

        switch (event.type) {
            case IntegrationChangeTypes.SCHOOL_LIST:

                const currentOrg = this.builder.request.requestedOrganizations
                    .find((org: RequestedOrganizationModel) => org.edutone.identifier === event.object.schoolGuid);

                if (currentOrg && (event.action === 'update')) {
                    if (!currentOrg.requestedAction) {
                        currentOrg.requestedAction = RequestedAction.UPDATE;
                    } else {
                        if (currentOrg.requestedAction === RequestedAction.UPDATE) {
                            currentOrg.requestedAction = null;
                        }
                    }
                } else {
                    rSchool = this.builder.buildSchool(event.object);
                    if (this.builder[event.action]) {
                        this.builder[event.action](rSchool);
                    }
                }

                break;
            case IntegrationChangeTypes.ATTR_CHANGED:
                event.object.attr.sameMappedAttributes.forEach((attr: AttributeEntityItem) => {
                    this.requestedAttrChanged(attr, event);
                });
                break;
            case IntegrationChangeTypes.PII_SHIELD_UPDATE:
                event.object.attr.sameMappedAttributes.forEach((attr: AttributeEntityItem) => {

                    let currentRequestedTask = this.builder.request.requestedTasks.find((currTask: RequestedTaskModel) =>
                        (currTask.displayType === event.object.entity.displayType));
                        // (currTask.sourceType === event.object.entity.sourceType)
                        // && (currTask.targetType === event.object.entity.targetType));
                    this.requestedAttrChanged(attr, event);

                    if (!currentRequestedTask) {
                        currentRequestedTask = this.builder.request.requestedTasks.find((currTask: RequestedTaskModel) =>
                            (currTask.displayType === event.object.entity.displayType));
                        // (currTask.sourceType === event.object.entity.sourceType)
                        // && (currTask.targetType === event.object.entity.targetType));
                    }

                    // check if attribute was really changed
                    if (this.integration.state !== IntegrationState.ORIGINAL) {

                        const updatedCurrentRequestedAttr = currentRequestedTask.requestedAttributes
                            .find((currAttr: RequestedAttributeModel) => currAttr.mappingId === event.object.attr.mappingId);
                        if (updatedCurrentRequestedAttr
                            && updatedCurrentRequestedAttr.requestedAction
                            && (updatedCurrentRequestedAttr.requestedAction === RequestedAction.PII_SHIELD_UPDATE)) {

                            this.integration.tasks
                                .filter((savedTask: IntegrationTask) =>
                                    (savedTask.sourceType === event.object.entity.sourceType)
                                    && (savedTask.targetType === event.object.entity.targetType))
                                .forEach((savedRequestTask: IntegrationTask) => {

                                    const savedMapping = savedRequestTask.mappings.find((mapping: IntegrationTaskMap) =>
                                        mapping.mappingId === event.object.attr.mappingId);

                                    if (savedMapping && (savedMapping.piiShieldAction === updatedCurrentRequestedAttr.piiShieldAction)) {

                                        // delete attr because it wasn't really changed
                                        currentRequestedTask.requestedAttributes = currentRequestedTask.requestedAttributes
                                            .filter((currAttr: RequestedAttributeModel) =>
                                                currAttr.mappingId !== event.object.attr.mappingId);

                                        if (currentRequestedTask.requestedAttributes.length === 0) {
                                            // delete task with no attributes
                                            this.builder.request.requestedTasks = this.builder.request.requestedTasks
                                                .filter((currTask: RequestedTaskModel) =>
                                                    currTask.requestedAttributes.length > 0);
                                        }
                                    }
                                });
                        }
                    }
                });
                break;
            case IntegrationChangeTypes.SCHOOL_ROLE:
                this.builder.activatedRolesForSchools(event.object);
                break;
        }
    }

    requestedAttrChanged(attr: AttributeEntityItem, event: IntegrationChangeEvent): void {
        const rAttr = this.builder.buildTaskAttr(attr);
        const rTask = this.builder.buildTask(attr.attrEntity);
        if (event.object.changeDataMasking && !attr.enabled) {
            rAttr.piiShieldAction = null;
        }
        const actionToTrigger = (event.action === 'piiShieldUpdate') && (rTask.requestedAction === RequestedAction.ADD)
            ? RequestedAction.ADD.toLowerCase()
            : event.action;
        if (this.builder[actionToTrigger]) {
            this.builder[actionToTrigger](rAttr, rTask);
        }
    }

    handlerFiltersChanges(event: FilteringChangeEvent) {
        let rTask: RequestedTaskModel, rAttr: RequestedAttributeModel, rRule: RequestedFilterModel;
        let rSchool: RequestedOrganizationModel, rFilter: RequestedPropertyModel;

        switch (event.type) {
            case FiltersChangeTypes.RULE_CHANGED:
                rRule = this.builder.buildRule(event.object.rule);
                if (this.builder[event.action]) {
                    this.builder[event.action](rRule);
                }
                break;
            case FiltersChangeTypes.FILTER_CHANGED:
                rRule = this.builder.buildRule(event.object.rule);
                rFilter = this.builder.buildFilter(event.object.filter);
                if (this.builder[event.action]) {
                    this.builder[event.action](rFilter, rRule);
                }
                break;
            case FiltersChangeTypes.FILTER_ATTR_CHANGED:
                break;
            case FiltersChangeTypes.FILTER_PREDICATE_CHANGED:
                break;
            case FiltersChangeTypes.FILTER_VALUE_CHANGED:
                break;
        }

        console.log(this.builder.forIntegration(this.integration).build());
    }
}

export type IntegrationModelClasses =
| RequestedOrganizationModel
| RequestedTaskModel
| RequestedAttributeModel
| RequestedFilterModel
| RequestedPropertyModel
| RequestedArgumentModel;

export class DataAccessRequestBuilder {

    request: DataAccessRequestModel;
    private attrCache: Map<string, RequestedTaskModel> = new Map();
    private ruleCache: Map<string, RequestedFilterModel> = new Map();
    private filterCache: Map<string, RequestedPropertyModel> = new Map();
    isRequestFromOriginal: boolean;

    buildOrg(orgGuid: string, orgName: string): RequestedOrganizationModel {
        const obj = new RequestedOrganizationModel();
        obj.customOrgName = orgName;
        obj.source = null;
        obj.edutone = new OrgIdentity(orgGuid, orgName);
        obj.target = null;
        return obj;
    }

    buildSchool(org: SchoolProfileModel): RequestedOrganizationModel {
        return this.buildOrg(org.schoolGuid, org.schoolName);
    }

    buildTask(entity: AttributeEntity): RequestedTaskModel {
        const gkey = entity.sourceType + '-' + entity.targetType;
        let obj: RequestedTaskModel = this.attrCache.get(gkey);
        if (!obj) {
            obj = new RequestedTaskModel();
            obj.taskId = this.isRequestFromOriginal ? gkey : entity.taskId;
            obj.sourceType = entity.sourceType;
            obj.targetType = entity.targetType;
            obj.displayType = entity.displayType;
            obj.csvFileName = entity.csvFileName;
            obj.requestedAttributes = [];
            this.attrCache.set(gkey, obj);
        }
        return obj;
    }

    buildTaskAttr(attribute: AttributeEntityItem): RequestedAttributeModel {
        const obj: RequestedAttributeModel = new RequestedAttributeModel();
        obj.mappingId = attribute.mappingId;
        obj.sourcePropertyName = attribute.sourcePropertyName;
        obj.targetPropertyName = attribute.targetPropertyName;
        obj.backToSourcePropertyName = attribute.backToSourcePropertyName;
        obj.friendlyName = attribute.friendlyName;
        obj.piiShieldAction = attribute.piiShieldAction;
        return obj;
    }

    buildRule(rule: RestrictionRule): RequestedFilterModel {
        let obj: RequestedFilterModel = this.ruleCache.get(rule.uniqId);
        if (!obj) {
            obj = new RequestedFilterModel();
            obj.taskId = null;
            obj.action = FilterAction.ALLOW;
            obj.schoolGuid = null;
            obj.entityFilterId = rule.entityFilterId;
            obj.taskIdentifier = null;
            obj.eduToneEntityType = null;
            obj.propertyFilters = [];
            this.ruleCache.set(rule.uniqId, obj);
        }
        return obj;
    }

    buildFilter(filter: RestrictionFilter): RequestedPropertyModel {
        let obj: RequestedPropertyModel = this.filterCache.get(filter.uniqId);
        if (!obj) {
            obj = new RequestedPropertyModel();
            obj.propertyFilterId = filter.rule.entityFilterId;
            obj.propertyName = filter.attribute ? filter.attribute.propertyName : '';
            obj.predicateName = filter.predicate ? <FilterPredicate>filter.predicate.name : <FilterPredicate>'';
            obj.arguments = [];
            this.filterCache.set(filter.uniqId, obj);
        }
        return obj;
    }

    buildFilterVal(arg: SuggestionModel): RequestedArgumentModel {
        const obj = new RequestedArgumentModel();
        obj.id = arg.id;
        obj.name = arg.name;
        return obj;
    }

    constructor(
        private integration: Integration
    ) {
        this.request = new DataAccessRequestModel();
        this.isRequestFromOriginal = ([IntegrationState.ORIGINAL, IntegrationState.PENDING].indexOf(this.integration.state) > -1);
        this.appAssignmentMode(this.integration.appAssignmentMode);
    }

    vendorName(orgName: string): DataAccessRequestBuilder {
        this.request.vendor = orgName;
        return this;
    }
    providerName(orgName: string): DataAccessRequestBuilder {
        this.request.provider = orgName;
        return this;
    }
    sender(orgGuid: string): DataAccessRequestBuilder {
        this.request.senderOrgGuid = orgGuid;
        return this;
    }
    recipient(orgGuid: string): DataAccessRequestBuilder {
        this.request.recipientOrgGuid = orgGuid;
        return this;
    }
    message(message: string): DataAccessRequestBuilder {
        this.request.requestMessage = message;
        return this;
    }
    requestedFilters(requestedFilters: RequestedFilterModel[]): DataAccessRequestBuilder {
        this.request.requestedFilters = requestedFilters;
        return this;
    }
    ssoRequestedFilters(ssoRequestedFilters: RequestedFilterModel[]): DataAccessRequestBuilder {
        this.request.ssoRequestedFilters = ssoRequestedFilters;
        return this;
    }
    appAssignmentMode(ssoAppAccessMode: SsoAppAccessModes): DataAccessRequestBuilder {
        this.request.appAssignmentMode = ssoAppAccessMode;
        return this;
    }
    private applyChangesTo(val: CanAction, arr: CanAction[]) {
        if (val.getId()) {
            const fobj = arr.filter(i => (('getId' in i) && (i.getId() === val.getId())))[0];
            if (fobj) {
                const index = arr.indexOf(fobj, 0);
                if (index > -1) {
                    arr.splice(index, 1);
                }
                if ((fobj.requestedAction === RequestedAction.UPDATE)
                    || (val.requestedAction === fobj.requestedAction)
                    || (val.requestedAction === RequestedAction.PII_SHIELD_UPDATE)
                    // when we first make enabled Data masking disable and then make this attribute disabled
                    || ((val.requestedAction === RequestedAction.REMOVE) && (fobj.requestedAction === RequestedAction.PII_SHIELD_UPDATE))
                ) {
                    arr.push(val);
                }
            } else {
                arr.push(val);
            }
        }
    }

    private applyChanges(val: IntegrationModelClasses, p1?: IntegrationModelClasses) {
        if (val instanceof RequestedOrganizationModel) {
            this.applyChangesTo(val, this.request.requestedOrganizations);
        } else if (val instanceof RequestedTaskModel) {
            val.requestedAction = this.isRequestFromOriginal ? RequestedAction.ADD : RequestedAction.UPDATE;
            this.applyChangesTo(val, this.request.requestedTasks);
        } else if (val instanceof RequestedAttributeModel) {
            const task = <RequestedTaskModel>p1;
            task.requestedAction = this.isRequestFromOriginal ? RequestedAction.ADD : RequestedAction.UPDATE;
            this.applyChangesTo(task, this.request.requestedTasks);

            const attr = <RequestedAttributeModel>val;
            this.applyChangesTo(attr, task.requestedAttributes);
        } else if (val instanceof RequestedFilterModel) {
            const rule = <RequestedFilterModel>val;
            this.applyChangesTo(rule, this.request.requestedFilters);
        } else if (val instanceof RequestedPropertyModel) {
            const filter = <RequestedPropertyModel>val;
            const rule = <RequestedFilterModel>p1;
            this.applyChangesTo(filter, rule.propertyFilters);
        } else if (val instanceof RequestedArgumentModel) {
            const arg = <RequestedArgumentModel>val;
            const filter = <RequestedPropertyModel>p1;
            this.applyChangesTo(arg, filter.arguments);
        }
    }

    add(val: IntegrationModelClasses, p1?: IntegrationModelClasses): DataAccessRequestBuilder {
        val.requestedAction = RequestedAction.ADD;
        this.applyChanges(val, p1);
        return this;
    }
    remove(val: IntegrationModelClasses, p1?: IntegrationModelClasses): DataAccessRequestBuilder {
        val.requestedAction = RequestedAction.REMOVE;
        this.applyChanges(val, p1);
        return this;
    }
    update(val: IntegrationModelClasses, p1?: IntegrationModelClasses): DataAccessRequestBuilder {
        val.requestedAction = RequestedAction.UPDATE;
        this.applyChanges(val, p1);
        return this;
    }
    piiShieldUpdate(val: IntegrationModelClasses, p1?: IntegrationModelClasses): DataAccessRequestBuilder {
        val.requestedAction = RequestedAction.PII_SHIELD_UPDATE;
        this.applyChanges(val, p1);
        return this;
    }

    forIntegration(baseIntegration: Integration) {
        this.request.integrationId = baseIntegration.integrationId;
        return this;
    }

    files(files: DataAccessRequestFileLightModel[]) {
        this.request.files = files;
        return this;
    }

    activatedRolesForSchools(activatedRolesForSchools: RequestedSchoolRoles) {
        this.request.activatedRolesForSchools = activatedRolesForSchools;
        return this;
    }

    build(): DataAccessRequestModel {
        return this.request;
    }
}

export interface DataAttributeShort {
    propertyName: string;
    friendlyName: string;
    requestedAction?: RequestedAction;
    hasMapping: boolean;
}

export interface TaskDataRequirementsView {
    type: string;
    skipAttributes: DataAttributeShort[];
    reportAttributes?: DataAttributeShort[];
    skipAttributesObj?: {}; // { propertyName: DataAttributeShort }
}
